import {
  ConsoleOutputLevel,
  DepID_OpenInsightsFetch,
  DepID_PlatformTestObjectBeforeBeacon,
  DepID_PlatformTestObjectBeforeTest,
  DepID_PlatformTestObjectOnError,
  DepID_PlatformTestObjectSendDopplerBeacon,
  DepID_PlatformTestObjectSendPulsarBeacon,
  OutputToConsoleFunc,
  SessionMetadata,
} from "../@types"
import { ApplicationEvents } from "./applicationEvents"
import makeResourceTimingReportPayload, {
  Version as beaconPayloadVersion,
} from "./makeResourceTimingReportPayload"
import makeResourceURL from "./makeResourceURL"
import { Scheduler } from "./measurementScheduler"
import { FetchTestResult } from "./openinsights/fetch"
import {
  PerformanceEntryAwaitTimeout,
  PerformanceEntryManager,
  RegisteredCallbackNotConsumedError,
} from "./openinsights/resourceTiming"
import { PlatformTestObject } from "./platformTestObject"
import { ResettableEventCounter } from "./resettableEventCounter"
import { RuntimeValues } from "./runtimeValues"
import { fetch as OpenInsightsFetch } from "./openinsights/fetch"
import { Executable } from "./openinsights/start"
import { encode as messagePackEncode } from "../vendor/msgpack/encode"

export interface Dependencies {
  applicationEvents: ApplicationEvents
  callClearTimeout: (depID: number, timeoutID: number) => void
  callFetch: (url: string, options?: RequestInit) => Promise<Response>
  callSendBeacon: (depID: number, url: string) => boolean
  callSetTimeout: (depID: number, fn: () => void, timeout: number) => number
  callClearResourceTimings: () => void
  measurementScheduler: Scheduler
  newAbortController: () => AbortController
  newDate: (depID: number) => Date
  newEpochTimestamp: (depID: number) => number
  newMonotonicTimestamp: (depID: number) => number
  newRandomNumber: (depID: number) => number
  outputToConsole: OutputToConsoleFunc
  performanceEntryAwaitTimeouts: ResettableEventCounter
  performanceEntryManager: PerformanceEntryManager
  runtimeValues: RuntimeValues
}

export interface TestSetupResult {
  /**
   * Passed as an ISO-formatted date in the beacon data as a *rough* and
   * generally untrustworthy estimate of when the measurement was performed.
   */
  calledAt: Date
}

export type PlatformTestObjectResultBundle = FetchTestResult & {
  cdnResponseHeaderValue?: string
  testSetupResult: TestSetupResult
}

const newOnErrorMessage = (dopplerPlatformID: string, e: unknown, at: Date) =>
  `onError ${dopplerPlatformID} (${at.toISOString()}): ${e}`

type BeaconRequestHeaders = {
  "doppler-payload-version": string
  "content-type": string
}

export default function (
  deps: Dependencies,
  config: PlatformTestObject,
  sessionMetadata: SessionMetadata,
) {
  const resourceURL = makeResourceURL(
    `https://${config.testObjectHostname}`,
    config.testObjectPath,
    deps.newRandomNumber,
  )
  /**
   * Milliseconds elapsed since baseline timestamp
   */
  let testObjectDownloadMonotonicStartTime: number
  const result: Executable = {
    execute: () =>
      OpenInsightsFetch<PlatformTestObjectResultBundle>(
        {
          clearTimeout: (timeoutID: number) =>
            deps.callClearTimeout(DepID_OpenInsightsFetch, timeoutID),
          fetch: deps.callFetch,
          newAbortController: deps.newAbortController,
          newDate: () => deps.newDate(DepID_OpenInsightsFetch),
          performanceEntryManager: deps.performanceEntryManager,
          setTimeout: (fn: () => void, timeout: number) =>
            deps.callSetTimeout(DepID_OpenInsightsFetch, fn, timeout),
        },
        resourceURL,
        config.testObjectTimeout,
        null,
        // beforeTest
        () => {
          if (deps.runtimeValues.measurementsEnabled) {
            deps.applicationEvents.recordBeforeTestObjectDownloadEvent(
              deps.newEpochTimestamp(DepID_PlatformTestObjectBeforeTest),
              deps.runtimeValues,
              config,
            )
            testObjectDownloadMonotonicStartTime = deps.newMonotonicTimestamp(
              DepID_PlatformTestObjectBeforeTest,
            )
            return Promise.resolve({
              testSetupResult: {
                calledAt: deps.newDate(DepID_PlatformTestObjectBeforeTest),
              },
              timeout: false,
            })
          }
          return Promise.reject(
            new Error(
              "beforeTest: Measurement blocked (measurements disabled)",
            ),
          )
        },
        // beforeBeacon -- this is our opportunity to adorn the
        // test result as we see fit
        (testResult, response) => {
          deps.applicationEvents.recordAfterTestObjectDownloadEvent(
            deps.newDate(DepID_PlatformTestObjectBeforeBeacon).getTime(),
            deps.runtimeValues,
            config,
            Math.floor(
              deps.newMonotonicTimestamp(DepID_PlatformTestObjectBeforeBeacon) -
                testObjectDownloadMonotonicStartTime,
            ),
          )
          if (config.cdnResponseHeader) {
            const cdnName = response.headers.get(config.cdnResponseHeader)
            if (cdnName) {
              testResult.cdnResponseHeaderValue = cdnName
            }
          }
          return Promise.resolve()
        },
        // sendBeacon
        (testResult) => {
          try {
            sendDopplerBeacon(testResult)
            sendLegacyPulsarBeacon(testResult)
          } catch (_e) {
            // Consider application logging of specific events
          }
        },
        // afterTest
        (testResult) => {
          deps.measurementScheduler.scheduleNextMeasurement(config)
          deps.measurementScheduler.outputDiagnosticMessage()
          return Promise.resolve(testResult)
        },
        // onError
        (e) => {
          deps.outputToConsole(ConsoleOutputLevel.debug, () =>
            newOnErrorMessage(
              config.dopplerPlatformID,
              e,
              deps.newDate(DepID_PlatformTestObjectOnError),
            ),
          )
          if (e instanceof RegisteredCallbackNotConsumedError) {
            // This means another measurement is still underway
            deps.measurementScheduler.scheduleNextMeasurementSoon(config)
          } else if (e instanceof PerformanceEntryAwaitTimeout) {
            // Generally an issue with the Resource Timing buffer,
            // mainly in Firefox.
            // For browsers that shut down tracking further performance
            // events until the buffer is cleared, now is an acceptable
            // time to do that.
            // Arbitrary number of errors to allow before clearing the
            // buffer again
            deps.performanceEntryAwaitTimeouts.add(1)
            if (deps.performanceEntryAwaitTimeouts.countOfEvents > 5) {
              deps.callClearResourceTimings()
              deps.performanceEntryAwaitTimeouts.reset()
            }
            // Reschedule normally
            deps.measurementScheduler.scheduleNextMeasurementSoon(config)
          }
        },
      ),
  }
  return result

  function sendDopplerBeacon(testResult: PlatformTestObjectResultBundle) {
    const beaconRequestMonotonicStartTime = deps.newMonotonicTimestamp(
      DepID_PlatformTestObjectSendDopplerBeacon,
    )
    deps.applicationEvents.recordBeforeMeasurementBeaconEvent(
      deps.newEpochTimestamp(DepID_PlatformTestObjectSendDopplerBeacon),
      deps.runtimeValues,
    )
    const headers: BeaconRequestHeaders = {
      "doppler-payload-version": beaconPayloadVersion,
      "content-type": "application/json",
    }

    const payload = makeResourceTimingReportPayload(
      resourceURL,
      config,
      testResult,
      sessionMetadata,
      deps.runtimeValues,
    )
    try {
      const options = {
        method: "POST",
        // Explicitly allow request to outlive page similar to sendBeacon
        keepalive: true,
        headers: new Headers(headers),
        body: messagePackEncode(payload),
      }
      deps
        .callFetch(
          config.clientEndpoints.dopplerEndpoints.dopplerResourceTimingEndpoint,
          options,
        )
        .then((response) => {
          const continent = response.headers.get("beacon-continent")
          const country = response.headers.get("beacon-country")
          const subdiv = response.headers.get("beacon-subdiv")
          const asn = response.headers.get("beacon-asn")
          if (continent) {
            deps.runtimeValues.lastReadContinentCodeHeader = continent
          }
          if (country) {
            deps.runtimeValues.lastReadCountryCodeHeader = country
          }
          if (subdiv) {
            deps.runtimeValues.lastReadCountrySubdivCodeHeader = subdiv
          }
          if (asn) {
            deps.runtimeValues.lastReadAsnHeader = asn
            deps.runtimeValues.updateMeasurementsEnabledForNetwork(asn)
          }
          deps.applicationEvents.recordAfterMeasurementBeaconEvent(
            deps.newEpochTimestamp(DepID_PlatformTestObjectSendDopplerBeacon),
            deps.runtimeValues,
            Math.floor(
              deps.newMonotonicTimestamp(
                DepID_PlatformTestObjectSendDopplerBeacon,
              ) - beaconRequestMonotonicStartTime,
            ),
          )
        })
        .catch((e) => {
          if (e instanceof Error) {
            deps.applicationEvents.recordMeasurementBeaconErrorEvent(
              deps.newEpochTimestamp(DepID_PlatformTestObjectSendDopplerBeacon),
              deps.runtimeValues,
              e.message,
            )
          }
        })
    } catch (_e) {
      // Consider application logging of specific events
    }
  }

  function sendLegacyPulsarBeacon(testResult: PlatformTestObjectResultBundle) {
    if (config.includePulsar) {
      if (testResult.error || testResult.status !== 200) {
        const intervalInMilliseconds = Math.floor(
          deps.newMonotonicTimestamp(DepID_PlatformTestObjectSendPulsarBeacon) -
            testObjectDownloadMonotonicStartTime,
        )
        // Not sure if NS1 would rather have 650 here or the real
        // non-200 status
        deps.callSendBeacon(
          DepID_PlatformTestObjectSendPulsarBeacon,
          makeBeaconURL(650, intervalInMilliseconds),
        )
      } else {
        const entry = testResult.entry as PerformanceResourceTiming
        deps.callSendBeacon(
          DepID_PlatformTestObjectSendPulsarBeacon,
          makeBeaconURL(
            testResult.status,
            // TTLB
            Math.floor(entry.responseEnd - entry.requestStart),
          ),
        )
      }
    }

    function makeBeaconURL(httpStatus: number, value: number) {
      const randomString = Math.floor(
        deps.newRandomNumber(DepID_PlatformTestObjectSendPulsarBeacon) *
          (4294967295 + 1),
      )
      // eslint-disable-next-line max-len
      return `${config.clientEndpoints.pulsarEndpointBase}&a=${config.pulsarAppID}&x=${randomString}&r=${config.pulsarJobID}:x|${httpStatus}|${value}`
    }
  }
}
